/**
 * useBatterySimulation Hook
 *
 * Calls the battery-simulate edge function to run a perfect-foresight DP
 * over the site's load + Octopus Agile prices for the requested window.
 *
 * Returns the SimulateResult { param_hash, cached, computed_at, params,
 * monthly[], aggregate }. Results are cached server-side by param_hash;
 * pass forceRecompute to bypass the cache.
 *
 * Note: the DP assumes perfect price foresight, so the saving represents a
 * ceiling — a realisable day-ahead-only dispatcher would be lower.
 */

import { useState, useEffect } from 'react'
import { supabase } from '@/supabase-client'
import { BATTERY_PRESETS } from '@/lib/batteryPresets'

const DEFAULT_START = '2024-04-01'

/**
 * Default battery = first preset in BATTERY_PRESETS (currently Sungrow
 * SH15T + SBH400). Single source of truth so preset edits propagate.
 */
export const DEFAULT_BATTERY = { ...BATTERY_PRESETS[0].params }

const ALT_STANDING_DEFAULTS = {
  agile: 59.16,
  trio: 71.7891,
}

export function useBatterySimulation({
  start = DEFAULT_START,
  end,
  battery = DEFAULT_BATTERY,
  tariff = 'agile',
  regionCode = 'N',
  meterId = null,
  fixedUnitPence,
  fixedStandingPence,
  altStandingPence,
  forceRecompute = false,
  enabled = true,
} = {}) {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState(null)

  // Serialise battery to a stable dep string so object identity doesn't
  // re-trigger the call on every render.
  const batteryKey = JSON.stringify(battery)

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function run() {
      setIsLoading(true)
      setError(null)

      const body = {
        start_date: start,
        end_date: end ?? new Date().toISOString().slice(0, 10),
        region_code: regionCode,
        meter_id: meterId,
        tariff,
        battery,
        force_recompute: forceRecompute,
      }
      if (typeof fixedUnitPence === 'number') body.fixed_unit_pence = fixedUnitPence
      if (typeof fixedStandingPence === 'number') body.fixed_standing_pence = fixedStandingPence
      body.alt_standing_pence = typeof altStandingPence === 'number'
        ? altStandingPence
        : ALT_STANDING_DEFAULTS[tariff] ?? ALT_STANDING_DEFAULTS.agile

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'battery-simulate',
        { body }
      )

      if (cancelled) return

      if (fnError) {
        console.error('[useBatterySimulation] edge function error:', fnError)
        setError(fnError.message ?? 'battery-simulate failed')
        setData(null)
      } else {
        setData(result ?? null)
      }
      setIsLoading(false)
    }

    run()
    return () => { cancelled = true }
    // batteryKey stands in for battery to avoid re-firing on fresh object literals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    start, end, tariff, regionCode, meterId, batteryKey,
    fixedUnitPence, fixedStandingPence, altStandingPence,
    forceRecompute, enabled,
  ])

  return { data, isLoading, error }
}

export default useBatterySimulation
